// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import type { SpokenAudio } from './spoken-audio.js';
import { messages, type Language } from '../i18n.js';
import type { ApiResult, OperationDto, TurnResponseDto } from '../api/client.js';
import type { LiveEnd, LiveTranscriptHandlers, LiveTranscriptSession } from './live-transcript.js';
import type { AutoCommitRule } from './live-transcript.js';

/**
 * The live voice conversation (founder-directed): speak, be heard, hear the reply, speak again —
 * hands-free. Under the surface it is the same turn pipeline as typing: the Guard runs first, the
 * reply is verified before anything is shown or spoken, and the loop is strictly turn-based — the
 * microphone is closed while Bilal speaks, exactly as the no-barge-in rule requires.
 *
 * What is spoken is only what the speech doorway already permits (approved prose, named by
 * operation id). Deterministic safety wording and outage wording are DISPLAYED in the overlay and
 * never synthesized; a crisis reply ends the loop with the session, because that is what a crisis
 * does. Every dependency is injected, so the whole conversation loop runs under test with scripted
 * pieces and no browser.
 */
export type VoiceLoopState =
  | { readonly kind: 'connecting' }
  | {
      readonly kind: 'listening';
      readonly level: number;
      readonly committed: string;
      readonly interim: string;
    }
  | { readonly kind: 'thinking'; readonly stage: string | null }
  | { readonly kind: 'speaking'; readonly level: number }
  /** The provider's human recitation of the verse that stands beside the reply. */
  | { readonly kind: 'reciting'; readonly reference: string; readonly level: number }
  /** Wording shown, deliberately not spoken: safety scripts, outages, refusals. */
  | { readonly kind: 'notice'; readonly text: string }
  | {
      readonly kind: 'ended';
      readonly reason: 'exited' | 'unavailable' | 'session_closed' | 'failed';
    };

export const VOICE_AUTO_COMMIT: AutoCommitRule = Object.freeze({
  silenceMs: 1000,
  minSpeechMs: 350,
  speechRms: 0.02,
  releaseRms: 0.012,
  floorRatio: 2.5,
  maxUtteranceMs: 30_000,
});

const T = messages({
  ar: {
    sendFailed: 'تعذّر إرسال ما قلته. جرّب مرة أخرى.',
    replyFailed: 'تعذّر إكمال الرد. لنجرّب من جديد.',
    audioFailed: 'الرد وصل نصًا؛ تعذّر تشغيل الصوت الآن.',
    hadithAttached: (grade: string | null) =>
      `أُرفق حديث${grade === null ? '' : ` (${grade})`} مع الرد — تجده نصًّا في المحادثة.`,
  },
  en: {
    sendFailed: 'What you said could not be sent. Try again.',
    replyFailed: 'The reply could not be completed. Let us try again.',
    audioFailed: 'The reply arrived as text; the audio could not play just now.',
    hadithAttached: (grade: string | null) =>
      `A hadith${grade === null ? '' : ` (${grade})`} came with the reply — you will find it as text in the conversation.`,
  },
});

export interface VoiceLoopDeps {
  /** Which language the notices speak. The loop is not a component and cannot read the context. */
  readonly language?: Language | undefined;
  readonly startTranscript: (
    handlers: LiveTranscriptHandlers,
    autoCommit: AutoCommitRule,
  ) => LiveTranscriptSession;
  readonly commitTurn: (
    transcript: string,
    clientTurnId: string,
  ) => Promise<ApiResult<{ readonly id: string }>>;
  readonly awaitOperation: (
    operationId: string,
    onStage: (stage: string | null) => void,
  ) => Promise<ApiResult<OperationDto>>;
  readonly speak: (operationId: string, part?: 'prose' | 'link') => Promise<ApiResult<SpokenAudio>>;
  readonly play: (
    audio: SpokenAudio,
    onLevel: (level: number) => void,
    signal: AbortSignal,
  ) => Promise<void>;
  /** Plays the verse card's human recitation URL; same abort, same real levels. */
  readonly playRecitation: (
    url: string,
    onLevel: (level: number) => void,
    signal: AbortSignal,
  ) => Promise<void>;
  /** Display wording for a non-normal turn, or null for an ordinary spoken reply. */
  readonly noticeFor: (turn: TurnResponseDto) => string | null;
  /** Every delivered turn joins the conversation the person returns to after voice mode. */
  readonly onTurn: (userText: string, turn: TurnResponseDto, operationId: string) => void;
  readonly onState: (state: VoiceLoopState) => void;
  readonly newClientTurnId: () => string;
  readonly wait: (ms: number) => Promise<void>;
  readonly noticeHoldMs?: number;
}

export interface VoiceLoop {
  exit(): void;
}

export function startVoiceLoop(deps: VoiceLoopDeps): VoiceLoop {
  const t = T[deps.language ?? 'ar'];
  let active = true;
  // Read through a call: `exit()` flips the flag across awaits, which straight-line narrowing
  // cannot see — the analyzer would otherwise call every later check dead.
  const stopped = (): boolean => !active;
  let current: LiveTranscriptSession | null = null;
  const playAbort = new AbortController();

  const emit = (state: VoiceLoopState): void => {
    if (!stopped() || state.kind === 'ended') deps.onState(state);
  };

  const listenOnce = (): Promise<LiveEnd> =>
    new Promise<LiveEnd>((resolve) => {
      let committed = '';
      let interim = '';
      emit({ kind: 'connecting' });
      current = deps.startTranscript(
        {
          onLive: () => {
            emit({ kind: 'listening', level: 0, committed, interim });
          },
          onCommitted: (text) => {
            committed = text;
            emit({ kind: 'listening', level: 0, committed, interim });
          },
          onInterim: (text) => {
            interim = text;
            emit({ kind: 'listening', level: 0, committed, interim });
          },
          onLevel: (level) => {
            emit({ kind: 'listening', level, committed, interim });
          },
          onEnded: (end) => {
            current = null;
            resolve(end);
          },
        },
        VOICE_AUTO_COMMIT,
      );
    });

  const run = async (): Promise<void> => {
    while (!stopped()) {
      const heard = await listenOnce();
      if (stopped()) return;
      if (heard.kind === 'unavailable') {
        emit({ kind: 'ended', reason: 'unavailable' });
        return;
      }
      if (heard.kind === 'cancelled') return;
      if (heard.kind === 'failed') {
        emit({ kind: 'ended', reason: 'failed' });
        return;
      }
      const transcript = heard.transcript.trim();
      // Silence or an unintelligible capture costs nothing: listen again.
      if (transcript.length === 0) continue;

      emit({ kind: 'thinking', stage: null });
      const committed = await deps.commitTurn(transcript, deps.newClientTurnId());
      if (stopped()) return;
      if (!committed.ok) {
        if (committed.code === 'session_closed') {
          emit({ kind: 'ended', reason: 'session_closed' });
          return;
        }
        emit({ kind: 'notice', text: t.sendFailed });
        await deps.wait(deps.noticeHoldMs ?? 2500);
        continue;
      }
      const operation = await deps.awaitOperation(committed.value.id, (stage) => {
        emit({ kind: 'thinking', stage });
      });
      if (stopped()) return;
      const delivered = operation.ok ? operation.value.response : null;
      if (delivered === null) {
        emit({ kind: 'notice', text: t.replyFailed });
        await deps.wait(deps.noticeHoldMs ?? 2500);
        continue;
      }
      deps.onTurn(transcript, delivered, committed.value.id);

      const notice = deps.noticeFor(delivered);
      if (notice !== null) {
        // Shown, never spoken. A crisis closes the session, so the loop ends with it.
        emit({ kind: 'notice', text: notice });
        await deps.wait(deps.noticeHoldMs ?? 3500);
        if (delivered.safetyAction === 'crisis_script') {
          emit({ kind: 'ended', reason: 'session_closed' });
          return;
        }
        continue;
      }

      emit({ kind: 'speaking', level: 0 });
      const spoken = await deps.speak(committed.value.id);
      if (stopped()) return;
      if (spoken.ok) {
        await deps.play(
          spoken.value,
          (level) => {
            emit({ kind: 'speaking', level });
          },
          playAbort.signal,
        );
      } else {
        emit({ kind: 'notice', text: t.audioFailed });
        await deps.wait(deps.noticeHoldMs ?? 2500);
      }
      // The sources take their turn after the words (founder arc), each in the order shown: a
      // verse plays as the reciter's own voice; a hadith is text by rule, so it is announced and
      // read on screen. A reply may carry more than one (founder, 2026-09-11, item 13).
      for (const card of delivered.sourceCards) {
        if (!active || stopped()) break;
        // The server decides whether a recitation may play (delivery: `canPlayRecitation`);
        // the voice loop must not re-derive that rule any more than the card renderer does.
        if (card.canPlayRecitation && card.humanRecitationUrl !== null) {
          emit({ kind: 'reciting', reference: card.canonicalReference, level: 0 });
          try {
            await deps.playRecitation(
              card.humanRecitationUrl,
              (level) => {
                emit({ kind: 'reciting', reference: card.canonicalReference, level });
              },
              playAbort.signal,
            );
          } catch {
            /* the card stays in the conversation; the loop simply continues */
          }
        } else if (card.kind === 'hadith') {
          emit({
            kind: 'notice',
            text: t.hadithAttached(card.grade),
          });
          await deps.wait(deps.noticeHoldMs ?? 3000);
        }
      }
      // The flow's second half (founder direction): after the source has had its own voice, Bilal
      // explains in his own words why it belongs here. Ordinary approved prose only; a missing or
      // unspeakable link simply ends the reply.
      if (typeof delivered.sourceLinkProse === 'string' && delivered.sourceLinkProse.length > 0) {
        const link = await deps.speak(committed.value.id, 'link');
        if (stopped()) return;
        if (link.ok) {
          emit({ kind: 'speaking', level: 0 });
          await deps.play(
            link.value,
            (level) => {
              emit({ kind: 'speaking', level });
            },
            playAbort.signal,
          );
        }
      }
      // Loop: the reply has finished, the microphone opens again.
    }
  };

  void run();

  return {
    exit(): void {
      if (stopped()) return;
      active = false;
      current?.cancel();
      playAbort.abort();
      deps.onState({ kind: 'ended', reason: 'exited' });
    },
  };
}
